<template>
    <div class="rp-helpdesk-customerjourney-prechat-form">
        <div
            class="form"
            :disabled="isDisabled"
            :style="{
                borderTopColor: activeColor
            }"
        >
            <div class="qualifying-properties">
                <div
                    v-for="property in fields"
                    :key="property.name"
                    class="qualifying-property"
                >   
                    <template v-if="property.value">
                        <div>
                            <Label :increaseBy="2" bolder="500" color="#333333">{{ $t('inputPropDisplayNames.'+property.name)}} {{$t('inputFields.submitted')}}:</Label>
                            <VMargin :unit=".10" />
                            {{ property.value }}
                            <HMargin
                                :unit=".15"
                            />
                            <span
                                :style="{ color: '#85ce61' }"
                            >
                                <i class="el-icon-success"></i>
                            </span>
                        </div>
                    </template>
                    <template v-else>
                        <FormItem 
                            :property="property"
                            :showAllDataTypes="showAllDataTypes"
                            @handleClick="handleClick"
                            :showSubmit="false"
                            @focus="handleFocus"
                            @handleEnter="handleEnter"
                            @handleError="updateFormErrorStatus"
                            :popoverWidth="popoverWidth"
                        />
                    </template>
                    <VMargin />
                </div>
                <div class="rp-scenario-buttons">
                    <primary-button
                        ref="scenario-prechat-submit-button"
                        :class="{'rp-form-error': isError, 'uppercase-text': true }"
                        :buttonStyle="{color: activeColor, borderColor: activeColor}"
                        @click="handleSubmit"
                        size="medium"
                        :disabled="isError">
                        {{ submitButtonText }}
                    </primary-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
import MediaTypeText from './MediaTypeText';
import FormItem from './MediaSubForm/FormItem';
import StartConversationButton from '../StartConversationButton'
import PrimaryButton from './../../shared/PrimaryButton';
export default {
    components: {
        FormItem,
        StartConversationButton,
        PrimaryButton
    },
    props: {

        formConfiguration: {
            type: Object,
            required: true
        },
        formHeaderLabel: {
            type: String,
            required: true,
        },
        submitButtonlabel: {
            type: String,
            default: "Start a chat"
        },
        disableProceed: {
            type: Boolean,
            default: false
        },
        showAllDataTypes: {
            type: Boolean,
            default: false
        },
        userProperties: {
            type: Object,
            default: {},
            required: true
        },
        popoverWidth: {
            type: Number,
            default: 340
        }
    },

    data() {
        return {
            text: '',
            disabled: false,
            fields: {},
            isLoadingMap: {},
            EMAIL_REGEX: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            PHONE_REGEX: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
            isFormSubmitting: false,
            isError: false,
            submitButtonText: this.submitButtonlabel
        }
    },

    mounted() {
        this.updateFields();
    },

    watch: {
        ['formConfiguration.fields'](val) {
            this.updateFields();
        }
    },

    computed: {

        isDisabled() {
            return Object.values(this.fields)
                .some(({ isLoading }) => !!isLoading);
        },
        
        propertiesObject() {
			const o = {};
			this.properties.forEach(prop => o[prop.propertyName] = prop);
			return o;
        },
        conversationPropertiesObj() {
            const o = {};
            this.conversationProperties.forEach(prop => o[prop.propertyName] = prop);
            return o;
        },

        value() {
            return this.formConfiguration.mediaValue;
        },

        ...mapGetters({
            properties: 'tenantProperties',
            conversationProperties: 'conversationProperties'
        }),
        activeColor() {
		try {
                return this.$root.configuration.style.activeColor || '#004e96'
            }
            catch(e) {
                return '#004e96'
            }
        }

    },

    methods: {
        showAlertOnFormCTA (message = this.submitButtonlabel) {
			this.isError = true
			this.updateSubmitButtonText(message)
			setTimeout(() => {
				this.isError = false
				this.resetSubmitButtonText()
			}, 2500)
        },
        resetSubmitButtonText () {
			this.submitButtonText = this.submitButtonlabel
        },
        updateSubmitButtonText(message) {
            this.submitButtonText = message
        },
        updateFormErrorStatus(errorStatus) {
            this.isError = errorStatus
        },
        updateFields() {
            const { userProperties, conversationPropertiesObj, propertiesObject } = this;
            this.formConfiguration.fields.forEach(({ propertyName: name, value, groupName, required}) => {
                let type, displayName, dropdownOption, dropdownListOption;
                if(groupName === 'Conversation Data' && conversationPropertiesObj[name]) {
                    type = conversationPropertiesObj[name]['type'];
                    displayName = conversationPropertiesObj[name]['displayName'];
                    dropdownOption = conversationPropertiesObj[name]['dropdownOption'] || [];
                    dropdownListOption = conversationPropertiesObj[name]['dropdownListOption'] || [];
                } else {
                    type = propertiesObject[name]['type'];
                    displayName = propertiesObject[name]['displayName'];
                    dropdownOption = propertiesObject[name]['dropdownOption'] || [];
                    dropdownListOption = propertiesObject[name]['dropdownListOption'] || [];
                }

                const field = this.fields[name];
                let vModelValue = '';
                if(field && field['vModelValue']){
                    vModelValue = field['vModelValue'];
                } else {
                    if(groupName === "People Data") {
                        vModelValue = userProperties[name] || ''
                    }
                }
                if(type === "boolean") {
                    vModelValue = vModelValue || false
                }
                if (field) {
                    this.$set(this.fields, name, { name, displayName, value, groupName, vModelValue, type, dropdownOption: dropdownListOption || dropdownOption || [], required });
                } else {
                    this.$set(this.fields, name, { name, displayName, value: '', groupName, vModelValue, type, dropdownOption: dropdownListOption || dropdownOption || [], required });
                }
            });
        },
        checkDataValidation(property) {
            if(property) {
                const { name, vModelValue, required } = property
                if(name === 'email') {
                    if(this.EMAIL_REGEX.test(vModelValue)){
                        return true
                    } else {
                        if(!required && !vModelValue){
                            return true
                        }
                        return false
                    }
                }
                else if(name === 'phone'){
                    if(this.PHONE_REGEX.test(vModelValue)){
                        return true
                    } else {
                        if(!required && !vModelValue){
                            return true
                        }
                        return false
                    }
                }
            } else {
                if(Array.isArray(vModelValue) && vModelValue.length === 0){
							this.isError = true;
							this.$set(property, 'isError', true);
							return false;
					}
					else if (!(vModelValue) && required) {
							this.isError = false;
							this.$set(property, 'isError', true);
							return false;
					}
					else {
							this.isError = false;
							this.$set(property, 'isError', false);
							return true;
					}
            }
            return true
        },

        handleClick(property) {
            const { checkDataValidation } = this;
            if(checkDataValidation(property)) {
                const { vModelValue } = property;
                if (!(vModelValue && vModelValue.trim())) {
                    return;
                }
                this.$set(property, 'isLoading', true);
                this.$emit('submit', { type: 'form', message: this.message, data: { property } });
            } else {

            }
        
        },
        handleFocus(property) {
            //don't show error message on  focus
            this.isError = false;
            this.$set(property, 'isError', false);
        },
        handleEnter(property) {
            this.handleSubmit()
        },

        handleSubmit() {
            const { checkDataValidation, disableProceed } = this;
            const { fields } = this;
            let formValid = true;
            if(this.disableProceed) {
                this.showAlertOnFormCTA(this.$t('orderList.selectItemsErrorMsg'))
                this.$emit('updateErrorMessage', true);
            }
            for(let propertyName in fields) {
                const property = fields[propertyName]
                if(checkDataValidation(property)) {
                    let { vModelValue, required, type } = property;
                    if(['multiSelect', 'list'].includes(type)) {
                        try {
                            vModelValue = JSON.parse(vModelValue)
                        } catch (error) {
                            vModelValue = vModelValue
                        }
                    }
                    if(Array.isArray(vModelValue) && vModelValue.length === 0){
                        formValid = false;
                        this.$set(property, 'isError', true);
                    }
                    else if (!(vModelValue) && required) {
                        formValid = false;
                        this.$set(property, 'isError', true);
                    }
                    else {
                        this.$set(property, 'isError', false);
                    }
                } else {
                    formValid = false;
                    //show validation error
                    this.$set(property, 'isError', true);
                }
            }
            if(disableProceed) {
                return ;
            }
            // set form error
            if(!formValid) {
                const firstErrorProp = fields[Object.keys(fields).find(fieldname => fields[fieldname].isError)]
                const errorMessage = `Please provide ${firstErrorProp.displayName.toLowerCase()}`
                this.showAlertOnFormCTA(errorMessage)
            }

            //when form is completely valid
            //submit the data to parent component
            if(formValid) {
                this.$emit('submit', { type: 'form', data: { fields } });
            }

            
        }

    }

}

</script>

<style lang="scss">

.rp-helpdesk-customerjourney-prechat-form {
    // overflow: auto; // Fixes the popper-append-to-body selector for being visible in the stack
    @include for-size(tablet-landscape-up){
        width: 500px;
        max-width: 100%;
        margin: 0 auto;
    }
    @include for-size(desktop-up){
        width: 640px;
        max-width: 100%;
        margin: 0 auto;
    }
    .form {
        h4{
            font-weight: 400;
            text-align: left;
            font-size: 18px;
            margin-top: 0px;
            margin-bottom: 1.5rem;
            color: #333333;
        }
        .label {
            font-size: 15px;
            font-weight: 500;
            cursor: pointer;
        }

        .input {
            margin-top: .75rem;

            .el-input {
                font-size: 14.5px;
                font-weight: $fontWeight;
            }

            .el-input__inner {
                height: 40px;
                line-height: 40px;
                 @include for-size(desktop-up){
                    width: 500px;
                    max-width: 100%;
                    font-size: 16.5px;
                }
            }
        }

        &[disabled="disabled"] {
            opacity: 0.5;
            pointer-events: none;
        }
    }
    .qualifying-properties{
        font-size:13px;
        color: #4d4d4d;
        @include for-size(desktop-up){
            width: 500px;
            max-width: 100%;
        }

        .qualifying-property {
            label {
                div:first-child {
                    font-size: 14px !important;
                    @include for-size(desktop-up){
                        font-size: 16px !important;
                    }
                    font-weight: 500 !important;
                    cursor: pointer;
                }
            }
            .input {
                margin-top: .5rem;

                .el-input {
                    font-size: 14.5px;
                    @include for-size(desktop-up){
                        font-size: 16.5px;
                    }
                    font-weight: $fontWeight;
                }

                .el-input__inner {
                    height: 40px;
                    line-height: 40px;
                    @include for-size(desktop-up){
                        height: 50px;
                        line-height: 50px;
                    }
                }
            }
        }
    }
    .rp-scenario-buttons {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 12px 0px 0px 0px;
        overflow: hidden;
        max-width: 360px;

        .scenario-button-text {
            font-weight: 500;
        }

        button {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: normal;
            line-height: 22px;
            height: auto;
            text-transform: uppercase;
            @include for-size(desktop-up){
                font-size: 16px;
            }
        }
    }

    .rp-helpdesk-conversation-message-get-email.value-submitted {
        .label {
            font-size: 10px;
        }
    }

    .rp-qualifying-properties-error-msg{
        color: #ff0000;
        margin-top:5px;
    }

}
</style>

<style lang="scss" scoped>
.rp-form-error {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
  background-color: #ff0000 !important;
  border-color:  #ff0000 !important;
  color: #fff !important;
  opacity: 0.8 !important;
  font-size: 14px;
  transition: 0.3s opacity ease-in;
  .text {
	//   animation: text-appear 0.5s
	// transition: 0.3s opacity ease-in;
	// transform: scaleY(1) translateZ(0);
	// transform-origin: 50% 50%;
  }
  &:hover, 
  &:focus  {
    background-color: #ff0000 !important;
    border-color:  #ff0000 !important;
    color: #fff !important;
  }
}

@keyframes shake {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
}

@keyframes text-appear {
//   0%, 12.66%, 100% {transform:translate3d(0,0,0);}
//   16.66%, 29.32% {transform:translate3d(0,-25%,0);}
//   33.32%,45.98% {transform:translate3d(0,-50%,0);}
//   49.98%,62.64% {transform:translate3d(0,-75%,0);}
//   66.64%,79.3% {transform:translate3d(0,-50%,0);}
//   83.3%,95.96% {transform:translate3d(0,-25%,0);}
	0% {
		transform: translateX(0);
		opacity: 0;
	}
	50% {
		transform: translateX(1.5);
	}
	100% {
		transform: translateX(1);
	}
}
</style>